/**
 * Resolve Catchball routing policy: Workflow → Cycle → Track.
 * Canon 29 §6. Parallel to session defaults inheritance (no Session layer).
 */

import type { Workflow, WorkflowConfiguration } from '../types/prioritization/workflow';
import type {
  BallSplitUnit,
  CatchballRoutingInheritanceBreakdown,
  CatchballRoutingKeyBreakdown,
  CatchballRoutingLayerOverride,
  CatchballRoutingSource,
  CatchballRoutingWorkflowConfig,
  EffectiveCatchballRouting,
  PathAdjustPolicy,
  ResolutionTarget,
} from '../types/prioritization/catchballRouting';
import { CANONICAL_CATCHBALL_ROUTING } from '../types/prioritization/catchballRouting';

const SPLIT_UNITS: BallSplitUnit[] = ['disabled', 'org_unit', 'virtual_recipient', 'track'];
const RESOLUTION_TARGETS: ResolutionTarget[] = [
  'split_node',
  'lowest_common_authority',
  'cycle_owner',
  'originating_requester',
];
const PATH_ADJUST: PathAdjustPolicy[] = ['none', 'holder_remaining_spine', 'cycle_owner_only'];

function asSplitUnit(v: unknown): BallSplitUnit | undefined {
  return typeof v === 'string' && SPLIT_UNITS.includes(v as BallSplitUnit)
    ? (v as BallSplitUnit)
    : undefined;
}

function asResolution(v: unknown): ResolutionTarget | undefined {
  return typeof v === 'string' && RESOLUTION_TARGETS.includes(v as ResolutionTarget)
    ? (v as ResolutionTarget)
    : undefined;
}

function asPathAdjust(v: unknown): PathAdjustPolicy | undefined {
  return typeof v === 'string' && PATH_ADJUST.includes(v as PathAdjustPolicy)
    ? (v as PathAdjustPolicy)
    : undefined;
}

/** Read snake_case workflow.configuration routing keys. */
export function routingFromWorkflowConfig(
  config: WorkflowConfiguration | null | undefined,
): Partial<EffectiveCatchballRouting> {
  if (!config || typeof config !== 'object') return {};
  const c = config as WorkflowConfiguration & CatchballRoutingWorkflowConfig;
  const out: Partial<EffectiveCatchballRouting> = {};
  if (typeof c.catchball_enabled === 'boolean') out.catchballEnabled = c.catchball_enabled;
  if (typeof c.rollout_enabled === 'boolean') out.rolloutEnabled = c.rollout_enabled;
  const unit = asSplitUnit(c.ball_split_unit);
  if (unit) out.ballSplitUnit = unit;
  if (typeof c.ball_split_max_children === 'number' && c.ball_split_max_children > 0) {
    out.ballSplitMaxChildren = Math.floor(c.ball_split_max_children);
  }
  if (typeof c.require_aggregate_before_pass_up === 'boolean') {
    out.requireAggregateBeforePassUp = c.require_aggregate_before_pass_up;
  }
  const rt = asResolution(c.resolution_target);
  if (rt) out.resolutionTarget = rt;
  const pa = asPathAdjust(c.path_adjust_policy);
  if (pa) out.pathAdjustPolicy = pa;
  return out;
}

/** Read camelCase metadata.catchballRouting overrides. */
export function routingFromMetadataLayer(
  raw: unknown,
): CatchballRoutingLayerOverride {
  if (!raw || typeof raw !== 'object') return {};
  const m = raw as Record<string, unknown>;
  const out: CatchballRoutingLayerOverride = {};
  if (typeof m.rolloutEnabled === 'boolean') out.rolloutEnabled = m.rolloutEnabled;
  const unit = asSplitUnit(m.ballSplitUnit);
  if (unit) out.ballSplitUnit = unit;
  if (typeof m.ballSplitMaxChildren === 'number' && m.ballSplitMaxChildren > 0) {
    out.ballSplitMaxChildren = Math.floor(m.ballSplitMaxChildren);
  }
  if (typeof m.requireAggregateBeforePassUp === 'boolean') {
    out.requireAggregateBeforePassUp = m.requireAggregateBeforePassUp;
  }
  const rt = asResolution(m.resolutionTarget);
  if (rt) out.resolutionTarget = rt;
  const pa = asPathAdjust(m.pathAdjustPolicy);
  if (pa) out.pathAdjustPolicy = pa;
  return out;
}

function layerToPartial(layer: CatchballRoutingLayerOverride): Partial<EffectiveCatchballRouting> {
  const out: Partial<EffectiveCatchballRouting> = {};
  if (layer.rolloutEnabled !== undefined) out.rolloutEnabled = layer.rolloutEnabled;
  if (layer.ballSplitUnit !== undefined) out.ballSplitUnit = layer.ballSplitUnit;
  if (layer.ballSplitMaxChildren !== undefined) out.ballSplitMaxChildren = layer.ballSplitMaxChildren;
  if (layer.requireAggregateBeforePassUp !== undefined) {
    out.requireAggregateBeforePassUp = layer.requireAggregateBeforePassUp;
  }
  if (layer.resolutionTarget !== undefined) out.resolutionTarget = layer.resolutionTarget;
  if (layer.pathAdjustPolicy !== undefined) out.pathAdjustPolicy = layer.pathAdjustPolicy;
  return out;
}

export function resolveEffectiveCatchballRouting(input: {
  workflow?: Workflow | null;
  cycleMetadata?: Record<string, unknown> | null;
  trackMetadata?: Record<string, unknown> | null;
}): EffectiveCatchballRouting {
  const workflowLayer = routingFromWorkflowConfig(input.workflow?.configuration);
  const cycleLayer = layerToPartial(
    routingFromMetadataLayer(input.cycleMetadata?.catchballRouting),
  );
  const trackLayer = layerToPartial(
    routingFromMetadataLayer(input.trackMetadata?.catchballRouting),
  );

  const base: EffectiveCatchballRouting = {
    ...CANONICAL_CATCHBALL_ROUTING,
    ...workflowLayer,
  };

  // Rollout requires catchball.
  const catchballEnabled = base.catchballEnabled !== false;

  const merged: EffectiveCatchballRouting = {
    catchballEnabled,
    rolloutEnabled:
      trackLayer.rolloutEnabled ??
      cycleLayer.rolloutEnabled ??
      base.rolloutEnabled,
    ballSplitUnit:
      trackLayer.ballSplitUnit ?? cycleLayer.ballSplitUnit ?? base.ballSplitUnit,
    ballSplitMaxChildren:
      trackLayer.ballSplitMaxChildren ??
      cycleLayer.ballSplitMaxChildren ??
      base.ballSplitMaxChildren,
    requireAggregateBeforePassUp:
      trackLayer.requireAggregateBeforePassUp ??
      cycleLayer.requireAggregateBeforePassUp ??
      base.requireAggregateBeforePassUp,
    resolutionTarget:
      trackLayer.resolutionTarget ??
      cycleLayer.resolutionTarget ??
      base.resolutionTarget,
    pathAdjustPolicy:
      trackLayer.pathAdjustPolicy ??
      cycleLayer.pathAdjustPolicy ??
      base.pathAdjustPolicy,
  };

  if (!catchballEnabled) {
    merged.rolloutEnabled = false;
  }
  if (merged.ballSplitUnit === 'disabled') {
    merged.rolloutEnabled = false;
  }

  return merged;
}

function pickSource(
  key: keyof Omit<EffectiveCatchballRouting, 'catchballEnabled'>,
  track: Partial<EffectiveCatchballRouting>,
  cycle: Partial<EffectiveCatchballRouting>,
  workflow: Partial<EffectiveCatchballRouting>,
): CatchballRoutingSource {
  if (track[key] !== undefined) return 'track';
  if (cycle[key] !== undefined) return 'cycle';
  if (workflow[key] !== undefined) return 'workflow';
  return 'canonical';
}

export function getCatchballRoutingInheritanceBreakdown(input: {
  workflow?: Workflow | null;
  cycleMetadata?: Record<string, unknown> | null;
  trackMetadata?: Record<string, unknown> | null;
}): CatchballRoutingInheritanceBreakdown {
  const workflowLayer = routingFromWorkflowConfig(input.workflow?.configuration);
  const cycleLayer = layerToPartial(
    routingFromMetadataLayer(input.cycleMetadata?.catchballRouting),
  );
  const trackLayer = layerToPartial(
    routingFromMetadataLayer(input.trackMetadata?.catchballRouting),
  );
  const effective = resolveEffectiveCatchballRouting(input);

  const keys: (keyof Omit<EffectiveCatchballRouting, 'catchballEnabled'>)[] = [
    'rolloutEnabled',
    'ballSplitUnit',
    'ballSplitMaxChildren',
    'requireAggregateBeforePassUp',
    'resolutionTarget',
    'pathAdjustPolicy',
  ];

  const rows: CatchballRoutingKeyBreakdown[] = keys.map((key) => ({
    key,
    value: effective[key],
    source: pickSource(key, trackLayer, cycleLayer, workflowLayer),
  }));

  return { effective, rows };
}

/** Whether the current actor may adjust the remaining path under this policy. */
export function canAdjustPathUnderPolicy(
  policy: PathAdjustPolicy,
  opts: { isBallHolder: boolean; isCycleOwnerOrEditor: boolean },
): boolean {
  switch (policy) {
    case 'none':
      return false;
    case 'holder_remaining_spine':
      return opts.isBallHolder;
    case 'cycle_owner_only':
      return opts.isCycleOwnerOrEditor;
    default:
      return false;
  }
}
